<script setup>
import { VForm } from 'vuetify/components/VForm'
import { themeConfig } from '@themeConfig'
import { usePlatformAdminAccountStore } from '@/stores/platformAdminAccount'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { safeAdminRedirectPath } from '@/utils/safeRedirect'

definePage({
  meta: {
    layout: 'blank',
    adminArea: true,
    unauthenticatedOnly: true,
  },
})

const { t } = useI18n()
const accountStore = usePlatformAdminAccountStore()
const route = useRoute()
const router = useRouter()

const isPasswordVisible = ref(false)
const formError = ref('')
const refVForm = ref()

const credentials = ref({
  email: '',
  password: '',
})

const login = async () => {
  formError.value = ''
  try {
    await accountStore.login(credentials.value)
    await router.replace(safeAdminRedirectPath(route.query.to))
  }
  catch (err) {
    formError.value = err.payload?.error || err.message || t('admin.login.genericError')
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid }) => {
    if (valid)
      login()
  })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard
      flat
      border
      max-width="480"
      width="100%"
      class="pa-2"
    >
      <VCardText>
        <div class="d-flex align-center gap-x-3 mb-6">
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <span class="text-h5 text-high-emphasis">{{ $t('admin.brand') }}</span>
        </div>

        <h1 class="text-h4 mb-1">
          {{ $t('admin.login.title') }}
        </h1>
        <p class="mb-0 text-medium-emphasis">
          {{ $t('admin.login.subtitle') }}
        </p>
      </VCardText>

      <VCardText>
        <VAlert
          v-if="formError"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ formError }}
        </VAlert>

        <VForm
          ref="refVForm"
          @submit.prevent="onSubmit"
        >
          <VTextField
            v-model="credentials.email"
            :label="$t('common.email')"
            type="email"
            autocomplete="username"
            class="mb-4"
            :rules="[v => !!v || $t('admin.login.emailRequired')]"
          />

          <VTextField
            v-model="credentials.password"
            :label="$t('common.password')"
            autocomplete="current-password"
            class="mb-6"
            :type="isPasswordVisible ? 'text' : 'password'"
            :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
            :rules="[v => !!v || $t('admin.login.passwordRequired')]"
            @click:append-inner="isPasswordVisible = !isPasswordVisible"
          />

          <VBtn
            block
            type="submit"
            color="primary"
            :loading="accountStore.loading"
          >
            {{ $t('admin.login.submit') }}
          </VBtn>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>
